// Where an expense happened.
//
// The rule: the Location on a posted expense follows where the expense ACTUALLY
// happened, selected by the person filing it — explicitly not derived from the
// employee's home office. So the claim captures a branch, and Finance binds each
// branch to a ledger Location once, PER LEGAL ENTITY.
//
// Confirmed by the first tenant to need it (Fern, 2026-10-02). What that tenant
// chose is their configuration: a tenant may run several entities, each with its
// own ledger and its own branch→Location bindings. Nothing here names a place.
//
// It is stored in `allocation`, the item's existing bag of validated external
// refs (department, cost_center, project, client …) plus display snapshots. That
// bag is SHARED, which is the whole reason this file exists: writing a location
// must never drop a department somebody else's screen put there.
import type { Branch } from '@/lib/hrApi';

export const BRANCH_KEY = 'branch';
export const BRANCH_LABEL_KEY = 'branch_label';

export type Allocation = Record<string, unknown>;

/**
 * What Finance should recognise in the ledger.
 *
 * A branch's `location` is the city and its `name` may be the office. Both are
 * shown when they differ, because an office name alone does not say where the
 * money was spent.
 */
export function branchLabel(b: Pick<Branch, 'name' | 'location'>): string {
  const name = (b.name ?? '').trim();
  const place = (b.location ?? '').trim();
  if (!place || place.toLowerCase() === name.toLowerCase()) return name;
  if (!name) return place;
  return `${name} · ${place}`;
}

/**
 * Merge a branch choice into an existing allocation.
 *
 * Clearing the choice REMOVES both keys instead of writing empty strings:
 * "nowhere was chosen" has to be absent, because the resolver treats an empty
 * binding as no answer and an empty string would post a blank Location.
 *
 * Returns undefined when nothing is left, since the API treats allocation as
 * optional and `{}` would be a pointless write.
 */
export function withBranch(
  allocation: Allocation | undefined,
  branchId: string,
  branches: Pick<Branch, 'id' | 'name' | 'location'>[],
): Allocation | undefined {
  const next: Allocation = { ...(allocation ?? {}) };
  const chosen = branches.find((b) => b.id === branchId);
  if (!branchId || !chosen) {
    delete next[BRANCH_KEY];
    delete next[BRANCH_LABEL_KEY];
  } else {
    next[BRANCH_KEY] = chosen.id;
    next[BRANCH_LABEL_KEY] = branchLabel(chosen);
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/** The branch currently on an item, for re-opening the form on an edit. */
export function readBranchId(allocation: Allocation | undefined): string {
  const v = allocation?.[BRANCH_KEY];
  return typeof v === 'string' ? v : '';
}

/**
 * What to show on a saved item.
 *
 * Prefers the stored snapshot, so a branch later renamed or deleted still reads
 * as what was chosen at the time rather than vanishing — the same reason the
 * accounting mappings keep `external_value_name`.
 */
export function readBranchLabel(
  allocation: Allocation | undefined,
  branches: Pick<Branch, 'id' | 'name' | 'location'>[] = [],
): string {
  const snapshot = allocation?.[BRANCH_LABEL_KEY];
  if (typeof snapshot === 'string' && snapshot.trim()) return snapshot.trim();
  const id = readBranchId(allocation);
  const live = branches.find((b) => b.id === id);
  return live ? branchLabel(live) : '';
}
